// =============================================================================
// Admin Announcements Management Page
// =============================================================================

import { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { adminService, type DealWithStats } from '../../services/admin.service';
import api from '../../services/api';
import { formatDateTime } from '../../utils/dateFormat';
import { ShieldExclamationIcon } from '@heroicons/react/24/outline';
import { usePermissions } from '../../hooks/usePermissions';

// =============================================================================
// Types
// =============================================================================

interface Announcement {
  id: string;
  subject: string;
  body: string;
  recipientType: 'ALL' | 'DEAL' | 'SPECIFIC';
  dealId?: string;
  dealName?: string;
  sentAt: string;
  emailSent: boolean;
  recipientCount: number;
  readCount: number;
  isArchived?: boolean;
}

// =============================================================================
// Component
// =============================================================================

export function AnnouncementsPage() {
  const { hasPermission, isLoaded: permsLoaded } = usePermissions();
  const canCreate = hasPermission('communications', 'create');
  const canDelete = hasPermission('communications', 'delete');
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [deals, setDeals] = useState<DealWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // View modal
  const [selectedAnnouncement, setSelectedAnnouncement] = useState<Announcement | null>(null);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<Announcement | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Filters
  const [filterDealId, setFilterDealId] = useState<string>('all');
  const [showArchived, setShowArchived] = useState(false);

  // Form state
  const [formSubject, setFormSubject] = useState('');
  const [formBody, setFormBody] = useState('');
  const [formRecipientType, setFormRecipientType] = useState<'ALL' | 'DEAL'>('ALL');
  const [formDealId, setFormDealId] = useState('');
  const [formSendEmail, setFormSendEmail] = useState(true);

  useEffect(() => {
    loadDeals();
    loadAnnouncements();
  }, []);

  const loadDeals = async () => {
    try {
      const data = await adminService.listDeals();
      setDeals(data);
    } catch (err) {
      console.error('Failed to load deals:', err);
    }
  };

  const loadAnnouncements = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get<{ success: boolean; data: Announcement[] }>('/admin/announcements');
      setAnnouncements(response.data.data || []);
    } catch (err) {
      setError('Failed to load announcements');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formSubject || !formBody) {
      setCreateError('Please fill in all required fields');
      return;
    }

    if (formRecipientType === 'DEAL' && !formDealId) {
      setCreateError('Please select a deal');
      return;
    }

    try {
      setCreating(true);
      setCreateError(null);

      await api.post('/admin/announcements', {
        subject: formSubject,
        body: formBody,
        recipientType: formRecipientType,
        dealId: formRecipientType === 'DEAL' ? formDealId : undefined,
        sendEmail: formSendEmail,
      });

      setShowCreateModal(false);
      resetForm();
      loadAnnouncements();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setCreateError(error.response?.data?.error || 'Failed to send announcement');
    } finally {
      setCreating(false);
    }
  };

  const resetForm = () => {
    setFormSubject('');
    setFormBody('');
    setFormRecipientType('ALL');
    setFormDealId('');
    setFormSendEmail(true);
    setCreateError(null);
  };

  // Use centralized PST date formatting
  const formatDate = formatDateTime;

  const getRecipientLabel = (announcement: Announcement) => {
    if (announcement.recipientType === 'ALL') return 'All Investors';
    if (announcement.recipientType === 'DEAL') return announcement.dealName || 'Specific Deal';
    return 'Selected Investors';
  };

  const handleArchive = async (id: string) => {
    try {
      await api.put(`/admin/announcements/${id}/archive`);
      loadAnnouncements();
      setSelectedAnnouncement(null);
    } catch (err) {
      console.error('Failed to archive announcement:', err);
    }
  };

  const handleDeleteClick = (announcement: Announcement) => {
    setDeleteTarget(announcement);
    setDeleteConfirmText('');
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget || deleteConfirmText.toLowerCase() !== 'proceed') return;
    setDeleting(true);
    try {
      await api.delete(`/admin/announcements/${deleteTarget.id}`);
      loadAnnouncements();
      setSelectedAnnouncement(null);
      setDeleteTarget(null);
    } catch (err) {
      console.error('Failed to delete announcement:', err);
    } finally {
      setDeleting(false);
    }
  };

  // Filter announcements
  const filteredAnnouncements = announcements.filter((a) => {
    if (!showArchived && a.isArchived) return false;
    if (showArchived && !a.isArchived) return false;
    if (filterDealId !== 'all' && a.dealId !== filterDealId) return false;
    return true;
  });

  // Permission guard - early return after all hooks
  if (permsLoaded && !hasPermission('communications', 'view')) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <ShieldExclamationIcon className="h-12 w-12 text-gray-400 mb-3" />
        <h3 className="text-lg font-medium text-gray-900">Access Denied</h3>
        <p className="mt-1 text-sm text-gray-500">You do not have permission to view this page.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Announcements</h1>
          <p className="text-gray-500 mt-1">Send announcements to investors</p>
        </div>
        {canCreate && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            New Announcement
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 bg-white p-4 rounded-lg shadow">
        <div className="flex-1">
          <label className="block text-xs font-medium text-gray-700 mb-1">Filter by Deal</label>
          <select
            value={filterDealId}
            onChange={(e) => setFilterDealId(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">All Announcements</option>
            {deals.map((deal) => (
              <option key={deal.id} value={deal.id}>{deal.name}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2 pt-5">
          <button
            onClick={() => setShowArchived(false)}
            className={`px-3 py-2 rounded-lg text-sm ${!showArchived ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-600'}`}
          >
            Active ({announcements.filter(a => !a.isArchived).length})
          </button>
          <button
            onClick={() => setShowArchived(true)}
            className={`px-3 py-2 rounded-lg text-sm ${showArchived ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-600'}`}
          >
            Archived ({announcements.filter(a => a.isArchived).length})
          </button>
        </div>
      </div>

      {/* Announcements List */}
      <div className="bg-white rounded-lg shadow">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-900" />
          </div>
        ) : error ? (
          <div className="text-center py-12 text-red-600">{error}</div>
        ) : filteredAnnouncements.length === 0 ? (
          <div className="text-center py-12">
            <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
            </svg>
            <p className="text-gray-500">{showArchived ? 'No archived announcements' : 'No announcements yet'}</p>
            {!showArchived && canCreate && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="mt-4 text-primary-600 hover:text-primary-800"
              >
                Send your first announcement
              </button>
            )}
          </div>
        ) : (
          <div className="divide-y">
            {filteredAnnouncements.map((announcement) => (
              <div
                key={announcement.id}
                className="p-4 hover:bg-gray-50 cursor-pointer"
                onClick={() => setSelectedAnnouncement(announcement)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">{announcement.subject}</h3>
                    <p className="text-gray-500 text-sm mt-1 line-clamp-2">
                      {announcement.body.replace(/<[^>]*>/g, '').substring(0, 150)}...
                    </p>
                    <div className="flex items-center gap-4 mt-2 text-sm text-gray-400">
                      <span>{formatDate(announcement.sentAt)}</span>
                      <span className="px-2 py-0.5 bg-gray-100 rounded">
                        {getRecipientLabel(announcement)}
                      </span>
                      {announcement.emailSent && (
                        <span className="flex items-center gap-1 text-green-600">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          Email sent
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-right ml-4">
                    <p className="text-sm text-gray-500">{announcement.recipientCount} recipients</p>
                    <p className="text-xs text-gray-400">{announcement.readCount} read</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Announcement Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold">New Announcement</h2>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {createError && (
                <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">
                  {createError}
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Recipients *</label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        value="ALL"
                        checked={formRecipientType === 'ALL'}
                        onChange={(e) => setFormRecipientType(e.target.value as 'ALL')}
                        className="text-primary-600 focus:ring-primary-500"
                      />
                      <span>All Investors</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        value="DEAL"
                        checked={formRecipientType === 'DEAL'}
                        onChange={(e) => setFormRecipientType(e.target.value as 'DEAL')}
                        className="text-primary-600 focus:ring-primary-500"
                      />
                      <span>Specific Deal</span>
                    </label>
                  </div>
                </div>

                {formRecipientType === 'DEAL' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Select Deal *</label>
                    <select
                      value={formDealId}
                      onChange={(e) => setFormDealId(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Select a deal</option>
                      {deals.map((deal) => (
                        <option key={deal.id} value={deal.id}>
                          {deal.name} ({deal.investorCount} investors)
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subject *</label>
                  <input
                    type="text"
                    value={formSubject}
                    onChange={(e) => setFormSubject(e.target.value)}
                    placeholder="Announcement subject"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
                  <textarea
                    value={formBody}
                    onChange={(e) => setFormBody(e.target.value)}
                    placeholder="Write your announcement..."
                    rows={8}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    required
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    You can use basic formatting. HTML tags are supported.
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="sendEmail"
                    checked={formSendEmail}
                    onChange={(e) => setFormSendEmail(e.target.checked)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <label htmlFor="sendEmail" className="text-sm text-gray-700">
                    Also send as email notification
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                  disabled={creating}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreate}
                  disabled={creating || !formSubject || !formBody}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {creating ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                      Send Announcement
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Announcement Modal */}
      {selectedAnnouncement && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-semibold">{selectedAnnouncement.subject}</h2>
                  <p className="text-sm text-gray-500 mt-1">
                    Sent {formatDate(selectedAnnouncement.sentAt)} to {getRecipientLabel(selectedAnnouncement)}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedAnnouncement(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="flex items-center gap-4 mb-4 text-sm">
                <span className="text-gray-500">{selectedAnnouncement.recipientCount} recipients</span>
                <span className="text-gray-500">{selectedAnnouncement.readCount} read</span>
                {selectedAnnouncement.emailSent && (
                  <span className="flex items-center gap-1 text-green-600">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Email sent
                  </span>
                )}
              </div>

              <div className="prose max-w-none border-t pt-4">
                <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedAnnouncement.body) }} />
              </div>

              <div className="flex justify-between mt-6 pt-4 border-t">
                <div className="flex gap-2">
                  {!selectedAnnouncement.isArchived && canDelete && (
                    <button
                      onClick={() => handleArchive(selectedAnnouncement.id)}
                      className="px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
                    >
                      Archive
                    </button>
                  )}
                  {canDelete && (
                    <button
                      onClick={() => handleDeleteClick(selectedAnnouncement)}
                      className="px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg"
                    >
                      Delete
                    </button>
                  )}
                </div>
                <button
                  onClick={() => setSelectedAnnouncement(null)}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete Announcement</h3>
            </div>

            <p className="text-sm text-gray-600 mb-2">
              You are about to permanently delete this announcement:
            </p>
            <p className="text-sm font-medium text-gray-900 mb-4 p-2 bg-gray-50 rounded-lg">
              {deleteTarget.subject}
            </p>
            <p className="text-sm text-red-600 mb-4">
              This action cannot be undone.
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type <span className="font-bold">proceed</span> below to confirm deletion:
              </label>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="Type 'proceed' to confirm"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                autoFocus
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setDeleteTarget(null);
                  setDeleteConfirmText('');
                }}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={deleteConfirmText.toLowerCase() !== 'proceed' || deleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {deleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    Deleting...
                  </>
                ) : (
                  'Delete Announcement'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AnnouncementsPage;
